import React, { useState } from 'react';
import { PlayCircle, Film, Filter, Calendar, Code, Database, Youtube, Cloud, ChevronDown, Clock } from 'lucide-react';


// --- Type Definitions (Unchanged) ---
interface Video {
  id: number;
  title: string;
  topic: 'Python' | 'React' | 'Design' | 'AWS/DevOps' | 'General';
  summary: string;
  date: string; // Format: YYYY-MM-DD
  videoUrl: string;
  duration: string; // e.g., "15:30"
  thumbnailUrl: string;
}

// --- Constants (Unchanged) ---
const videoBannerImage = "url('https://commverseglobal.com/wp-content/uploads/2024/04/case_study_banner.jpg')"; 

// Topic-specific colors and icons
const topicMap = {
  'Python': { icon: Code, color: 'text-green-600', border: 'border-green-600', hoverBg: 'hover:bg-green-100' },
  'React': { icon: Database, color: 'text-sky-500', border: 'border-sky-500', hoverBg: 'hover:bg-sky-100' },
  'Design': { icon: Film, color: 'text-pink-600', border: 'border-pink-600', hoverBg: 'hover:bg-pink-100' },
  'AWS/DevOps': { icon: Cloud, color: 'text-yellow-600', border: 'border-yellow-600', hoverBg: 'hover:bg-yellow-100' },
  'General': { icon: Youtube, color: 'text-red-600', border: 'border-red-600', hoverBg: 'hover:bg-red-100' },
};

// --- Mock Data (Unchanged) ---
const mockVideos: Video[] = [
  { id: 1, title: 'Complete React Project: E-commerce Website', topic: 'React', summary: 'Build a full-stack e-commerce site using functional components and hooks.', date: '2024-05-18', videoUrl: '#', duration: '2:10:45', thumbnailUrl: 'https://phswasco.com/wp-content/uploads/2024/02/AI-in-Education.webp' },
  { id: 2, title: 'Python for Beginners: Data Types and Loops', topic: 'Python', summary: 'A detailed tutorial covering Python fundamentals for new learners.', date: '2024-05-15', videoUrl: '#', duration: '45:30', thumbnailUrl: 'https://phswasco.com/wp-content/uploads/2024/02/AI-in-Education.webp' },
  { id: 3, title: 'Figma to Code: UI/UX Design Workflow', topic: 'Design', summary: 'Master the process of converting modern design mockups into clean code.', date: '2024-05-10', videoUrl: '#', duration: '1:05:00', thumbnailUrl: 'https://www.oreilly.com/api/v2/epubs/9780137870899/files/graphics/9780137870967.jpg' },
  { id: 4, title: 'Setting up CI/CD with AWS and Jenkins', topic: 'AWS/DevOps', summary: 'Step-by-step guide to automating deployments using Jenkins on AWS.', date: '2024-04-25', videoUrl: '#', duration: '35:12', thumbnailUrl: 'https://www.oreilly.com/api/v2/epubs/9780137870899/files/graphics/9780137870967.jpg' },
  { id: 5, title: 'Career Growth: How to Crack Technical Interviews', topic: 'General', summary: 'Essential tips and strategies for successful technical interview preparation.', date: '2024-04-01', videoUrl: '#', duration: '20:00', thumbnailUrl: 'https://static.vecteezy.com/system/resources/previews/001/879/648/non_2x/database-system-management-landing-page-design-manage-control-and-manage-data-access-to-databases-cloud-storage-network-chart-and-graph-illustration-for-poster-website-flyer-mobile-app-free-vector.jpg' },
  { id: 6, title: 'React State Management with Redux Toolkit', topic: 'React', summary: 'Advanced guide to Redux Toolkit for complex state management.', date: '2024-05-01', videoUrl: '#', duration: '55:00', thumbnailUrl: 'https://static.vecteezy.com/system/resources/previews/001/879/648/non_2x/database-system-management-landing-page-design-manage-control-and-manage-data-access-to-databases-cloud-storage-network-chart-and-graph-illustration-for-poster-website-flyer-mobile-app-free-vector.jpg' },
  { id: 7, title: 'Advanced Python: Decorators and Context Managers', topic: 'Python', summary: 'Deep dive into advanced Python features.', date: '2024-04-10', videoUrl: '#', duration: '30:00', thumbnailUrl: 'https://phswasco.com/wp-content/uploads/2024/02/AI-in-Education.webp' },
  { id: 8, title: 'A Quick Guide to Tailwind CSS v3', topic: 'Design', summary: 'A fast-paced tutorial on the new features in Tailwind CSS 3.', date: '2024-04-15', videoUrl: '#', duration: '18:40', thumbnailUrl: 'https://phswasco.com/wp-content/uploads/2024/02/AI-in-Education.webp' },
];


// --- Video Card Component (Unchanged from the previous best version) ---
const VideoCard: React.FC<{ video: Video }> = ({ video }) => {
  const { icon: TopicIcon, color } = topicMap[video.topic] || topicMap.General;
  
  const formattedDate = new Date(video.date).toLocaleDateString('en-IN', { year: 'numeric', month: 'short', day: 'numeric' });

  return (
    <div className="bg-white rounded-xl p-0 shadow-xl transition duration-300 transform hover:shadow-2xl hover:scale-[1.01] border border-gray-100">
      
      {/* Thumbnail and Play Icon */}
      <div className="relative overflow-hidden rounded-t-xl">
        <img 
          src={video.thumbnailUrl} 
          alt={video.title}
          className="w-full h-40 object-cover transition-transform duration-500 hover:scale-105"
        />
        <a 
          href={video.videoUrl} 
          target="_blank" 
          rel="noopener noreferrer"
          className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-30 transition-opacity opacity-0 hover:opacity-100"
        >
          <PlayCircle className="w-16 h-16 text-red-500 hover:text-red-400 transition transform hover:scale-110"/>
        </a>
        <span className="absolute bottom-2 right-2 bg-black bg-opacity-70 text-white text-xs font-semibold px-2 py-1 rounded-sm">
          <Clock className="w-3 h-3 inline mr-1"/> {video.duration}
        </span>
      </div>

      {/* Content Section */}
      <div className="p-5">
        
        <div className="mb-3">
          <h3 className="text-lg font-bold text-gray-900 mb-1 line-clamp-2">{video.title}</h3> {/* Font size slightly reduced */}
          <span className={`px-2 py-0.5 rounded text-xs font-medium bg-gray-100 ${color}`}>
             <TopicIcon className="w-3 h-3 inline mr-1"/> {video.topic}
          </span>
        </div>
        
        <p className="text-sm text-gray-600 mb-4 line-clamp-3">{video.summary}</p>
        
        <div className="flex justify-between items-center pt-3 border-t border-gray-100">
          
          <span className="flex items-center text-xs text-gray-500">
            <Calendar className="w-3 h-3 mr-1"/> Uploaded: {formattedDate}
          </span>
          
          <a 
            href={video.videoUrl} 
            target="_blank" 
            rel="noopener noreferrer"
            className={`inline-flex items-center px-3 py-1.5 text-sm font-semibold rounded-lg text-white bg-red-600 transition duration-150 shadow-lg 
              hover:bg-red-700`}
          >
            <Youtube className="w-4 h-4 mr-2"/> Watch
          </a>
        </div>
      </div>
    </div>
  );
};


// --- Main Component: Topic-wise Filter with Smaller Grid ---
const VideoPage: React.FC = () => {
  const [filterTopic, setFilterTopic] = useState<string>('All');
  const [sortBy, setSortBy] = useState<'date-desc' | 'date-asc' | 'title-asc'>('date-desc'); 

  const allTopics = ['All', ...Array.from(new Set(mockVideos.map(n => n.topic)))];

  // Filtering Logic
  const filteredVideos = mockVideos.filter(video => 
    filterTopic === 'All' || video.topic === filterTopic
  );

  // Sorting Logic (Unchanged)
  const sortedVideos = filteredVideos.sort((a, b) => {
    if (sortBy === 'date-desc') {
      return new Date(b.date).getTime() - new Date(a.date).getTime(); 
    }
    if (sortBy === 'date-asc') {
      return new Date(a.date).getTime() - new Date(b.date).getTime(); 
    }
    if (sortBy === 'title-asc') {
      return a.title.localeCompare(b.title); 
    }
    return 0;
  });


  return (
    <div className="min-h-screen bg-gray-50 pb-20">
      
      {/* 1. Page Header/Banner */}
      <div className="relative bg-gray-900 py-24 shadow-xl bg-cover bg-center" style={{ backgroundImage: videoBannerImage }}>
        <div className="absolute inset-0 bg-black opacity-70"></div>
        <div className="relative max-w-7xl mx-auto px-4 text-center text-white pt-10">
          <h1 className="text-4xl sm:text-5xl font-extrabold mb-3 tracking-wide text-red-500 font-aligran">
            My Video Tutorials 
          </h1>
          <p className="text-xl sm:text-2xl font-light max-w-4xl mx-auto">
            Browse our comprehensive video library by topic.
          </p>
        </div>
      </div>
      
      <div className="max-w-7xl mx-auto px-4 py-12">
        
        {/* 2. Controls (Filter & Sort) - Horizontal Layout */}
        <div className="bg-white p-6 rounded-xl shadow-lg mb-8 border-l-4 border-red-500">
            <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center">
                <Filter className="w-5 h-5 text-red-500 mr-2"/> Browse Videos ({sortedVideos.length})
            </h2>
            <div className="flex flex-col sm:flex-row justify-between items-center space-y-4 sm:space-y-0">
                
                {/* Topic Filters */}
                <div className="flex flex-wrap gap-2 items-center">
                    <span className="font-semibold text-gray-700 mr-2">Filter By Topic:</span>
                    {allTopics.map(topic => (
                        <button
                            key={topic}
                            onClick={() => setFilterTopic(topic)}
                            className={`px-3 py-1 text-sm rounded-md font-medium transition duration-200 border 
                                ${filterTopic === topic 
                                    ? 'bg-red-600 text-white border-red-600 shadow-sm' 
                                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                                }`}
                        >
                            {topic}
                        </button>
                    ))}
                </div>

                {/* Sorting Dropdown */}
                <div className="flex items-center space-x-2">
                    <Calendar className="w-5 h-5 text-red-600" />
                    <span className="font-semibold text-gray-700">Sort By:</span>
                    <div className="relative">
                        <select
                            value={sortBy}
                            onChange={(e) => setSortBy(e.target.value as 'date-desc' | 'date-asc' | 'title-asc')}
                            className="appearance-none bg-white border border-gray-300 text-gray-700 py-2 pl-3 pr-8 rounded-md shadow-sm focus:outline-none focus:ring-1 focus:ring-red-500"
                        >
                            <option value="date-desc">Newest First</option>
                            <option value="date-asc">Oldest First</option>
                            <option value="title-asc">Title (A-Z)</option>
                        </select>
                        <ChevronDown className="absolute right-2 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                    </div>
                </div>
            </div>
        </div>
      
        {/* 3. Videos Grid - ADJUSTED FOR SMALLER CARDS */}
        {sortedVideos.length === 0 ? (
            <p className="text-center text-xl text-gray-500 p-10">No videos found for the selected topic.</p>
        ) : (
            // Cards will be smaller due to lg:grid-cols-4 and xl:grid-cols-5
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 xl:grid-cols-5 gap-6"> 
                {sortedVideos.map(video => (
                    <VideoCard key={video.id} video={video} />
                ))}
            </div>
        )}
      </div>
      
    </div>
  );
};

export default VideoPage;